import React, {useEffect} from "react";
import {
    Row,
    Col,
    Switch,
    Card,
    InputNumber,
    Popconfirm,
    Table,
    Select,
    Tooltip,
    Upload,
    message,
    Spin,
    Button
} from "antd";
import {useFormik} from "formik";
import {useTranslation} from "react-i18next";
import {useStore} from "../../../../hooks/useStore";
import {Input} from "antd";
import * as Yup from "yup";
import {isNumeric} from "../../../../utils/helpers";
import {codPaymentSubcompanyPaymentPeriods} from "../../../../constants/constants";
import {InfoCircleOutlined, UploadOutlined} from "@ant-design/icons";
import {DocumentModal} from "../../../documents/documentModal";
import {useFetch} from "../../../../hooks/useFetch";
import {
    StyledCard,
    SwitchContainer,
    FormItem,
    UpdateButton
} from "./SubcompanyCodPaymentSettings.styles";
import {CodPaymentSelfSelectionFeatureForm} from "./codPaymentSelfSelectionFeatureForm/CodPaymentSelfSelectionFeatureForm";

const paymentInfoValidationSchema = Yup.object().shape({
    accountTitle: Yup.string().required("Hesap ünvanı zorunludur"),
    iban: Yup.string().required("IBAN zorunludur")
});

export const SubcompanyCodPaymentSettings = ({subCompany, tabKey, recall = () => {}}) => {
    const {t} = useTranslation();
    const stores = useStore();
    const [showDocModal, setShowDocModal] = React.useState(false);
    const [docUrl, setDocUrl] = React.useState("");
    const [uploading, setUploading] = React.useState(false);

    // useFetch ile sözleşme bilgisini çek
    const [
        contractLoading,
        contractList,
        setContractList,
        setContractLoading,
        refetchContractList
    ] = useFetch({
        defaultDataValue: [],
        fetchFn: () =>
            subCompany?._id
                ? stores.contractStore.getContractsBySubCompanyId({
                      subCompanyId: subCompany._id,
                      contractName: "codAgreement"
                  })
                : Promise.resolve({data: []}),
        dependencies: [subCompany?._id]
    });

    const contract = React.useMemo(
        () => contractList?.data?.find(c => c.contractName === "codAgreement") || null,
        [contractList]
    );

    const paymentMethodsFormik = useFormik({
        initialValues: {
            subCompanyId: subCompany?._id,
            vigoPos: subCompany?.options?.codPayment?.hasTerminalRight || false,
            creditCard: subCompany?.options?.codPayment?.allowedPaymentMethods?.creditCard || false,
            mealCard: subCompany?.options?.codPayment?.allowedPaymentMethods?.mealCard || false,
            cash: subCompany?.options?.codPayment?.allowedPaymentMethods?.cash || false,
            commissionRate: subCompany?.options?.codPayment?.commissionRate,
            paymentPeriodByDayBasis: subCompany?.options?.codPayment?.paymentPeriodByDayBasis
        },
        enableReinitialize: true,
        onSubmit: async values => {
            try {
                const codPaymentData = {
                    subCompanyId: values.subCompanyId,
                    hasTerminalRight: values.vigoPos,
                    allowedPaymentMethods: {
                        creditCard: values.creditCard,
                        mealCard: values.mealCard,
                        cash: values.cash
                    },
                    commissionRate:
                        typeof values.commissionRate === "number"
                            ? values.commissionRate
                            : undefined,
                    paymentPeriodByDayBasis:
                        typeof values.paymentPeriodByDayBasis === "number"
                            ? values.paymentPeriodByDayBasis
                            : undefined
                };

                const response = await stores.companyStore.updateSubCompanyCodPaymentSettings(
                    codPaymentData
                );
                if (response) {
                    recall();
                }
            } catch (error) {
                console.error("Form validation failed:", error);
            }
        }
    });

    const paymentInfoFormik = useFormik({
        initialValues: {
            subCompanyId: subCompany?._id,
            accountTitle: subCompany?.paymentInfo?.cashOnDelivery?.accountTitle || "",
            iban: subCompany?.paymentInfo?.cashOnDelivery?.iban || "",
            bankName: subCompany?.paymentInfo?.cashOnDelivery?.bankName || ""
        },
        validationSchema: paymentInfoValidationSchema,
        enableReinitialize: true,
        onSubmit: async values => {
            try {
                const payload = {
                    subCompanyId: values.subCompanyId,
                    paymentType: "cashOnDelivery",
                    accountTitle: values.accountTitle,
                    iban: values.iban
                };

                const response = await stores.companyStore.updateSubcompanyPaymentInfo({
                    ...payload
                });
                if (response) {
                    recall();
                }
            } catch (error) {
                console.error("Form validation failed:", error);
            }
        }
    });

    useEffect(() => {
        paymentMethodsFormik.resetForm();
        paymentInfoFormik.resetForm();
    }, [tabKey]);

    // Sözleşme yükleme fonksiyonu
    const handleUpload = async ({file}) => {
        if (!subCompany?._id) return;
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("contractName", "codAgreement");
            const resultOfRequest = await stores.contractStore.uploadDocumentToTemplateContract({
                subCompanyId: subCompany._id,
                formData
            });
            // Başarı kontrolü
            if (resultOfRequest?.success) {
                message.success("Sözleşme başarıyla yüklendi.");
                refetchContractList(); // Güncel veriyi çek
            } else {
                message.error(resultOfRequest?.message || "Sözleşme yüklenirken bir hata oluştu.");
            }
        } catch (e) {
            message.error(
                "Sözleşme yüklenirken bir hata oluştu: " + (e?.message || JSON.stringify(e))
            );
        } finally {
            setUploading(false);
        }
    };

    // Sözleşmeyi gösterme fonksiyonu
    const handleShowDocument = () => {
        const url = contract?.uploadedDocumentPath || contract?.files?.[0]?.path;
        setDocUrl(url);
        setShowDocModal(true);
    };

    return (
        <Row gutter={16}>
            <Col span={8}>
                <form onSubmit={paymentMethodsFormik.handleSubmit}>
                    <StyledCard title="Alt Firma Kapıda Ödeme Ayarları">
                        <SwitchContainer>
                            <div className="label">VigoPOS</div>
                            <Switch
                                name="vigoPos"
                                checked={paymentMethodsFormik.values.vigoPos}
                                onChange={checked =>
                                    paymentMethodsFormik.setFieldValue("vigoPos", checked)
                                }
                            />
                        </SwitchContainer>
                        <SwitchContainer>
                            <div className="label">Kredi Kartı</div>
                            <Switch
                                name="creditCard"
                                checked={paymentMethodsFormik.values.creditCard}
                                onChange={checked =>
                                    paymentMethodsFormik.setFieldValue("creditCard", checked)
                                }
                            />
                        </SwitchContainer>
                        <SwitchContainer>
                            <div className="label">Yemek Kartı</div>
                            <Switch
                                name="mealCard"
                                checked={paymentMethodsFormik.values.mealCard}
                                onChange={checked =>
                                    paymentMethodsFormik.setFieldValue("mealCard", checked)
                                }
                            />
                        </SwitchContainer>
                        <SwitchContainer>
                            <div className="label">Nakit</div>
                            <Switch
                                name="cash"
                                checked={paymentMethodsFormik.values.cash}
                                onChange={checked =>
                                    paymentMethodsFormik.setFieldValue("cash", checked)
                                }
                            />
                        </SwitchContainer>
                        <FormItem>
                            <div className="label">Ödeme Dönemi</div>
                            <Select
                                style={{width: "100%"}}
                                name="paymentPeriodByDayBasis"
                                value={paymentMethodsFormik.values.paymentPeriodByDayBasis}
                                onChange={value => {
                                    paymentMethodsFormik.setFieldValue(
                                        "paymentPeriodByDayBasis",
                                        value
                                    );

                                    if (value === 7) {
                                        paymentMethodsFormik.setFieldValue("commissionRate", 2.9);
                                    } else if (value === 30) {
                                        paymentMethodsFormik.setFieldValue("commissionRate", 1.8);
                                    }
                                }}
                            >
                                <Select.Option key={7} value={7}>
                                    {t(`other.codPaymentSubcompanyPaymentPeriods.weekly`, {
                                        ns: "common"
                                    })}
                                </Select.Option>
                                <Select.Option key={30} value={30}>
                                    {t("other.codPaymentSubcompanyPaymentPeriods.monthly", {
                                        ns: "common"
                                    })}
                                </Select.Option>
                            </Select>
                        </FormItem>
                        <FormItem>
                            <div className="label">Komisyon Oranı</div>
                            <InputNumber
                                style={{width: "100%"}}
                                name="commissionRate"
                                value={paymentMethodsFormik.values.commissionRate}
                                onChange={value => {
                                    paymentMethodsFormik.setFieldValue(
                                        "commissionRate",
                                        isNumeric(value) ? value : undefined
                                    );
                                }}
                                placeholder={`Komisyon oranı giriniz ( örnek: 2.9 )`}
                                min={0}
                            />
                        </FormItem>

                        <Row justify="end" style={{marginTop: "20px"}}>
                            <Popconfirm
                                title={
                                    <div>
                                        <p>Vigo POS ayarları güncellenecektir.</p>
                                        <Table
                                            pagination={false}
                                            size="small"
                                            key="property"
                                            dataSource={[
                                                {
                                                    property: "Vigo POS",
                                                    status: paymentMethodsFormik.values.vigoPos
                                                        ? "Aktif"
                                                        : "Pasif"
                                                },
                                                {
                                                    property: "Kredi Kartı",
                                                    status: paymentMethodsFormik.values.creditCard
                                                        ? "Aktif"
                                                        : "Pasif"
                                                },
                                                {
                                                    property: "Yemek Kartı",
                                                    status: paymentMethodsFormik.values.mealCard
                                                        ? "Aktif"
                                                        : "Pasif"
                                                },

                                                {
                                                    property: "Nakit",
                                                    status: paymentMethodsFormik.values.cash
                                                        ? "Aktif"
                                                        : "Pasif"
                                                },
                                                {
                                                    property: (
                                                        <>
                                                            Ödeme Dönemi{" "}
                                                            <Tooltip title="Kaç günde bir ödeme yapılacağını belirtir.">
                                                                <InfoCircleOutlined />
                                                            </Tooltip>
                                                        </>
                                                    ),
                                                    status: paymentMethodsFormik.values
                                                        .paymentPeriodByDayBasis
                                                        ? paymentMethodsFormik.values
                                                              .paymentPeriodByDayBasis
                                                        : "-"
                                                },
                                                {
                                                    property: "Komisyon Oranı",
                                                    status: isNumeric(
                                                        paymentMethodsFormik.values.commissionRate
                                                    )
                                                        ? paymentMethodsFormik.values.commissionRate
                                                        : "-"
                                                }
                                            ]}
                                        >
                                            <Table.Column title="Özellik" dataIndex="property" />
                                            <Table.Column title="Durum" dataIndex="status" />
                                        </Table>
                                    </div>
                                }
                                onConfirm={paymentMethodsFormik.handleSubmit}
                                disabled={paymentMethodsFormik.isSubmitting || !subCompany?._id}
                                cancelText="İptal"
                                okText="Evet"
                            >
                                <UpdateButton
                                    disabled={paymentMethodsFormik.isSubmitting || !subCompany?._id}
                                >
                                    {paymentMethodsFormik.isSubmitting
                                        ? "Güncelleniyor..."
                                        : "Güncelle"}
                                </UpdateButton>
                            </Popconfirm>
                        </Row>
                    </StyledCard>
                </form>
            </Col>

            <Col span={8}>
                <Row gutter={[16, 16]}>
                    <Col span={24}>
                        <form onSubmit={paymentInfoFormik.handleSubmit}>
                            <Card title="Geri Ödeme Bilgileri">
                                <FormItem>
                                    <div className="label">Hesap Ünvanı</div>
                                    <Input
                                        name="accountTitle"
                                        value={paymentInfoFormik.values.accountTitle}
                                        onChange={paymentInfoFormik.handleChange}
                                        onBlur={paymentInfoFormik.handleBlur}
                                        placeholder="Hesap ünvanı giriniz"
                                        status={
                                            paymentInfoFormik.touched.accountTitle &&
                                            paymentInfoFormik.errors.accountTitle
                                                ? "error"
                                                : ""
                                        }
                                    />
                                    {paymentInfoFormik.touched.accountTitle &&
                                        paymentInfoFormik.errors.accountTitle && (
                                            <div className="error">
                                                {paymentInfoFormik.errors.accountTitle}
                                            </div>
                                        )}
                                </FormItem>

                                <FormItem>
                                    <div className="label">IBAN</div>
                                    <Input
                                        name="iban"
                                        value={paymentInfoFormik.values.iban}
                                        onChange={paymentInfoFormik.handleChange}
                                        onBlur={paymentInfoFormik.handleBlur}
                                        placeholder="IBAN giriniz"
                                        status={
                                            paymentInfoFormik.touched.iban &&
                                            paymentInfoFormik.errors.iban
                                                ? "error"
                                                : ""
                                        }
                                    />
                                    {paymentInfoFormik.touched.iban &&
                                        paymentInfoFormik.errors.iban && (
                                            <div className="error">
                                                {paymentInfoFormik.errors.iban}
                                            </div>
                                        )}
                                </FormItem>

                                {paymentInfoFormik.values.bankName && (
                                    <FormItem>
                                        <div className="label">Banka Adı</div>
                                        <Input
                                            name="bankName"
                                            value={paymentInfoFormik.values.bankName}
                                            onChange={paymentInfoFormik.handleChange}
                                            onBlur={paymentInfoFormik.handleBlur}
                                            disabled
                                        />
                                    </FormItem>
                                )}

                                <Row justify="end" style={{marginTop: "20px"}}>
                                    <UpdateButton
                                        type="submit"
                                        disabled={
                                            paymentInfoFormik.isSubmitting ||
                                            !subCompany?._id ||
                                            !paymentInfoFormik.isValid ||
                                            !paymentInfoFormik.dirty
                                        }
                                    >
                                        {paymentInfoFormik.isSubmitting
                                            ? "Güncelleniyor..."
                                            : "Güncelle"}
                                    </UpdateButton>
                                </Row>
                            </Card>
                        </form>
                    </Col>
                    <Col span={24}>
                        <CodPaymentSelfSelectionFeatureForm
                            subCompany={subCompany}
                            recall={recall}
                            hasFormVisibilityChange={tabKey}
                        />
                    </Col>
                </Row>
            </Col>

            {subCompany?.options?.codPayment?.hasTerminalRight && (
                <Col span={8}>
                    <Card title="Kapıda Ödeme Sözleşmesi" style={{minHeight: 200}}>
                        {contractLoading ? (
                            <Spin tip="Yükleniyor..." />
                        ) : (
                            <>
                                <div>
                                    Fiziksel sözleşme yüklenme durumu:{" "}
                                    <b>
                                        {contract?.uploadedDocumentPath
                                            ? "Yüklenmiş"
                                            : "Yüklenmemiş"}
                                    </b>
                                    {contract?.uploadedDocumentPath && (
                                        <>
                                            <br />
                                            Yükleyen: <b>{contract.uploader}</b>
                                            <br />
                                            Yüklenme Tarihi:{" "}
                                            <b>
                                                {new Date(
                                                    contract.documentUploadedAt
                                                ).toLocaleString("tr-TR", {
                                                    day: "numeric",
                                                    month: "numeric",
                                                    year: "numeric",
                                                    hour: "2-digit",
                                                    minute: "2-digit"
                                                })}
                                            </b>
                                        </>
                                    )}
                                </div>
                                {contract?.uploadedDocumentPath ? (
                                    <Button
                                        type="primary"
                                        style={{marginTop: 16}}
                                        onClick={handleShowDocument}
                                    >
                                        Sözleşmeyi Görüntüle
                                    </Button>
                                ) : (
                                    <Upload
                                        customRequest={({file, onSuccess}) => {
                                            handleUpload({file});
                                            setTimeout(() => onSuccess && onSuccess("ok"), 0);
                                        }}
                                        showUploadList={false}
                                        accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                        disabled={uploading}
                                    >
                                        <Button
                                            icon={<UploadOutlined />}
                                            loading={uploading}
                                            style={{marginTop: 16}}
                                        >
                                            Sözleşmeyi Yükle
                                        </Button>
                                    </Upload>
                                )}
                            </>
                        )}
                    </Card>
                    <DocumentModal
                        visibility={showDocModal}
                        onCancel={() => setShowDocModal(false)}
                        documentUrl={docUrl}
                        title="Kapıda Ödeme Sözleşmesi"
                    />
                </Col>
            )}
        </Row>
    );
};
